// @flow
import React from 'react';
import ReactModal from 'react-modal';
const fixOrientation =  require('fix-orientation');

import './ImageCropper.css';

// Sniff if touch device
const isTouchDevice = !!(
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  ('ontouchstart' in window
    || (navigator.msMaxTouchPoints
      && typeof navigator.msMaxTouchPoints === 'number'
      && navigator.msMaxTouchPoints > 0)
  )
)

const MAX_CANVAS_WIDTH = 600;
const MAX_CANVAS_HEIGHT = 600;
const CROPPER_RADIUS = 150;
const ZOOM_LEVEL_MAX = 2;
const ZOOM_LEVEL_MIN = 0.2;
const ZOOM_STEP = 0.025;

const CUSTOM_MODAL_STYLE = {
  overlay: {
  },
  content: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  }
}

type ImageCropperProps = {
  isOpen: boolean;
  image: Blob;
  contentLabel: string;
};

/**
 * Image Cropper is instantiated after Avatar Input selects
 * a local file to crop.
 * 
 * @export
 * @class ImageCropper
 * @extends {React.Component}
 */
export default class ImageCropper extends React.Component {
  panStartX: number;
  panStartY: number;
  prevX: number;
  prevY: number;
  deltaX: number;
  deltaY: number;
  canvas: HTMLCanvasElement;

  state: {
    isOpen: boolean;
    image: Blob;
    sourceImage: Image;
    zoomLevel: number;
    imageX: number;
    imageY: number;
    imageRatio: number;
    rotation: number;
  }

  constructor(props: ImageCropperProps) {
    super(props);
    this.state = {
      isOpen: props.isOpen,
      image: props.image,
      sourceImage: new Image(),
      zoomLevel: 1,
      imageX: 0,
      imageY: 0,
      imageRatio: 0,
      rotation: 0
    };
    this.canvas = document.getElementsByTagName('canvas')[0];
    this.panStartX = 0;
    this.panStartY = 0;
    this.bootstrap = this.bootstrap.bind(this);
    this.displayImage = this.displayImage.bind(this);
    this.drawCropWindow = this.drawCropWindow.bind(this);
    this.draw = this.draw.bind(this);
    this.handleModalOpen = this.handleModalOpen.bind(this);
    this.handleZoomChange = this.handleZoomChange.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handlePanning = this.handlePanning.bind(this);
    this.saveCroppedImage = this.saveCroppedImage.bind(this);
  }

  /**
   * On component initialization with an image selected from AvatarInput
   * we need to scale the image to max size and center in the cropper
   * 
   * @memberof ImageCropper
   */
  bootstrap = () => {
    const fileReader = new FileReader();
    fileReader.onload = (evt) => {
      fixOrientation(evt.target.result, { image: true }, (fixed, img) => {
        const image = new Image();
        image.onload = () => {
          const imageRatio = Math.min(MAX_CANVAS_WIDTH / image.width, MAX_CANVAS_HEIGHT / image.height);
          const canvasDimensions = { width: imageRatio * image.width, height: imageRatio * image.height };
          this.canvas.width = canvasDimensions.width;
          this.canvas.height = canvasDimensions.height;
          this.setState({
            sourceImage: image,
            imageRatio
          }, () => {
            this.draw();
          });
        }
        image.src = fixed;
      });

    }
    fileReader.readAsDataURL(this.props.image);
  };

  displayImage = () => {
    // const scaledDimensions = this.getScaledImageDimensions();
    const imageWidth = (this.state.sourceImage && this.state.sourceImage.width) ? this.state.sourceImage.width : 0;
    const imageHeight = (this.state.sourceImage && this.state.sourceImage.height) ? this.state.sourceImage.height : 0;
    var hRatio = this.canvas.width / imageWidth;
    var vRatio = this.canvas.height / imageHeight || 0;
    var ratio = Math.min(hRatio, vRatio);
    this.context.drawImage(
      this.state.sourceImage,
      this.state.imageX,
      this.state.imageY,
      imageWidth * ratio * this.state.zoomLevel,
      imageHeight * ratio * this.state.zoomLevel
    );
  };

  drawCropWindow = () => {
    const radialGradient = this.context.createRadialGradient(
      this.canvas.width / 2,
      this.canvas.height / 2,
      this.canvas.width,
      this.canvas.width / 2,
      this.canvas.height / 2,
      CROPPER_RADIUS
    );
    radialGradient.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
    radialGradient.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
    radialGradient.addColorStop(1, 'transparent');
    this.context.fillStyle = radialGradient;
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.beginPath();
    this.context.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    this.context.setLineDash([5, 5]);
    this.context.arc(
      this.canvas.width / 2,
      this.canvas.height / 2,
      CROPPER_RADIUS,
      0,
      2 * Math.PI);
    this.context.stroke();
  };

  draw = () => {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.fillStyle = '#000';
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.displayImage();
    this.drawCropWindow();
  };

  handleCloseModal = (e: Event) => {
    this.setState({
      isOpen: false
    });
  };

  handleModalOpen = (e: Event) => {
    this.canvas = this.refs.imageCropperCanvas;
    this.context = this.canvas.getContext('2d');
    this.bootstrap();
  };

  handleModalRequestClose = (e: Event) => {

  };

  handleZoomChange = (evt: WheelEvent | Event & { target: HTMLInputElement }) => {
    let value = this.state.zoomLevel || 1;
    if (evt instanceof WheelEvent) {
      value = evt.deltaY > 0
        ? Math.min(value + ZOOM_STEP, ZOOM_LEVEL_MAX)
        : Math.max(value - ZOOM_STEP, ZOOM_LEVEL_MIN);
    } else {
      value = Number.parseFloat(evt.target.value, 10);
    }

    this.setState({
      zoomLevel: value
    });
    this.draw();
  };

  handlePanning = (evt: TouchEvent | MouseEvent) => {
    if (evt instanceof TouchEvent) {
      evt.preventDefault();
      this.setState({
        imageX: this.state.imageX + (evt.changedTouches[0].screenX - this.prevX),
        imageY: this.state.imageY + (evt.changedTouches[0].screenY - this.prevY)
      });
      this.prevX = evt.changedTouches[0].screenX;
      this.prevY = evt.changedTouches[0].screenY;
    } else {
      this.setState({
        imageX: this.state.imageX + evt.movementX,
        imageY: this.state.imageY + evt.movementY
      });
    }

    this.draw();
  };

  handleMouseDown = (evt: MouseEvent) => {
    evt.target.addEventListener('mousemove', this.handlePanning);
  };

  handleMouseUp = (evt: MouseEvent) => {
    evt.target.removeEventListener('mousemove', this.handlePanning);
  };

  handleTouchStart = (evt: TouchEvent) => {
    this.prevX = evt.touches[0].screenX;
    this.prevY = evt.touches[0].screenY;
    evt.target.addEventListener('touchmove', this.handlePanning, {
      passive: false,
      cancelable: true
    });
  };

  handleTouchEnd = (evt: TouchEvent) => {
    delete this.deltaX;
    delete this.deltaY;
    evt.target.removeEventListener('touchmove', this.handlePanning);
  };


  /**
   * Cropped image is going to take the original image and 
   * 
   * 
   * @memberof ImageCropper
   */
  saveCroppedImage = (): boolean => {
    // Create a new canvas to contain the cropped image only
    const croppedCanvas = document.createElement('canvas');
    const croppedContext = croppedCanvas.getContext('2d');
    croppedCanvas.width = MAX_CANVAS_WIDTH;
    croppedCanvas.height = MAX_CANVAS_HEIGHT;
    if (croppedContext) {
      // The scaling factor will change based on image aspect ratio
      const scalingFactor = this.state.sourceImage.width > this.state.sourceImage.height
        ? (this.state.sourceImage.width / MAX_CANVAS_WIDTH)
        : (this.state.sourceImage.height / MAX_CANVAS_HEIGHT);
      // This draws the cropped and scaled image to an offscreen context with all calculations for offset
      // due to cropping, and dimensions due to scaling maintining aspect ratio.
      croppedContext.drawImage(
        this.state.sourceImage,
        ((this.state.sourceImage.width / 2) - (CROPPER_RADIUS + this.state.imageX) * scalingFactor) / this.state.zoomLevel,
        ((this.state.sourceImage.height / 2) - (CROPPER_RADIUS + this.state.imageY) * scalingFactor) / this.state.zoomLevel,
        CROPPER_RADIUS * 2 * scalingFactor / this.state.zoomLevel,
        CROPPER_RADIUS * 2 * scalingFactor / this.state.zoomLevel,
        0,
        0,
        MAX_CANVAS_WIDTH,
        MAX_CANVAS_HEIGHT
      );
    }
    const base64String = croppedCanvas.toDataURL('image/png');
    this.props.setThumbnail(base64String);
    this.props.closeModal();
    return true;
  };

  render = () => {
    return (
      <ReactModal
        isOpen={this.props.isOpen}
        contentLabel={this.props.contentLabel}
        onAfterOpen={this.handleModalOpen}
        onRequestClose={this.handleModalRequestClose}
        style={CUSTOM_MODAL_STYLE}
      >
        <canvas
          ref='imageCropperCanvas'
          className='ImageCropper-canvas'
          onWheel={this.handleZoomChange}
          onMouseDown={this.handleMouseDown}
          onMouseLeave={this.handleMouseUp}
          onMouseUp={this.handleMouseUp}
          onTouchStart={this.handleTouchStart}
          onTouchCancel={this.handleTouchEnd}
          onTouchEnd={this.handleTouchEnd}
        >
        </canvas>
        <input
          type='range'
          name='zoomLevel'
          max={ZOOM_LEVEL_MAX}
          min={ZOOM_LEVEL_MIN}
          value={this.state.zoomLevel}
          onChange={this.handleZoomChange}
          step={ZOOM_STEP}
        />
        <button onClick={this.props.closeModal}>Cancel</button>
        <button onClick={this.saveCroppedImage}>Save</button>
      </ReactModal >
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ImageCropper/ImageCropper.jsx